import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { APP_ENVIRONMENT, APP_VERSION } from '@core/config/app-environment';
import {
  EventTrackerApp,
  EventTrackerEnv,
  EventTrackerJourneyStepName,
  TrackerEvent,
} from './event-tracker.model';
import { JourneyService } from './journey.service';
import { SessionService } from './session.service';

/**
 * Buffered event tracker (API_EVENT_TRACKER). Port of legacy EventTrackerService: same event
 * names, payloads, buffer size (10), flush interval (5 s) and unload flush.
 */
@Injectable({ providedIn: 'root' })
export class EventTrackerService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly sessionService = inject(SessionService);
  private readonly journeyService = inject(JourneyService);
  private readonly env = inject(APP_ENVIRONMENT);

  private readonly product = 'retiro' as const;
  private buffer: Partial<TrackerEvent>[] = [];
  private readonly BUFFER_SIZE = 10;
  private readonly FLUSH_INTERVAL_MS = 5000;

  constructor() {
    this.startFlushTimer();
    this.registerUnloadFlush();
  }

  track(partial: Partial<TrackerEvent>): void {
    try {
      const event = this.buildEvent(partial);
      this.buffer.push(event);
      if (this.buffer.length >= this.BUFFER_SIZE) {
        this.flush();
      }
    } catch {
      // Tracking must never block the business flow.
    }
  }

  /** Emits a `journey_step` event with all correlation fields. */
  trackStep(stepName: EventTrackerJourneyStepName, extra?: Partial<TrackerEvent>): void {
    this.track({ event_type: 'journey_step', event_name: stepName, ...extra });
  }

  /**
   * Sends the event without buffering. `useBeacon` is required when the caller reloads or
   * navigates right after: a normal HttpClient.post() is cancelled by the navigation.
   */
  trackImmediate(partial: Partial<TrackerEvent>, useBeacon = false): void {
    try {
      const event = this.buildEvent(partial);
      this.sendSingle(event, useBeacon);
    } catch {
      // Tracking must never block the business flow.
    }
  }

  /** Effectiveness event (legacy TrackService.trackEffectivenessEvent). */
  trackEffectivenessEvent(
    companyCode: string,
    storeCode: string,
    actorRole: string,
    clientKey: string,
    event: EventTrackerJourneyStepName,
  ): void {
    this.trackStep(event, {
      store_id: storeCode,
      result: 'success',
      client_key: clientKey,
      payload: {
        actor_role: actorRole,
        tenant_id: companyCode,
      },
    });
  }

  private buildEvent(partial: Partial<TrackerEvent>): TrackerEvent {
    const { tenantId, storeCode } = this.resolveJourneyContext();
    return {
      event_id: crypto.randomUUID(),
      event_ts: new Date().toISOString(),
      product: this.product,
      env: this.resolveEnv(),
      app: this.resolveApp(),
      app_version: APP_VERSION,
      session_id: this.sessionService.getSessionId(),
      route: this.router.url,
      source: 'web',
      // client_key from localStorage when present (= groupId of the withdrawal).
      client_key: this.journeyService.getJourneyId(tenantId, storeCode) ?? undefined,
      ...partial,
    } as TrackerEvent;
  }

  /** Extracts tenantId/storeCode from `/withdraws/:tenantId/:storeCode/...`. */
  private resolveJourneyContext(): { tenantId: string; storeCode: string } {
    try {
      const parts = window.location.pathname.split('/').filter(Boolean);
      if (parts[0] === 'withdraws') return { tenantId: parts[1] ?? '', storeCode: parts[2] ?? '' };
    } catch {
      /* ignored */
    }
    return { tenantId: '', storeCode: '' };
  }

  private resolveEnv(): EventTrackerEnv {
    if (this.env.production === 'true') return 'prod';
    return 'dev';
  }

  private resolveApp(): EventTrackerApp {
    const path = window.location.pathname;
    if (path.startsWith('/withdraws')) return 'customer';
    if (path.startsWith('/picking')) return 'picking';
    if (path.startsWith('/security')) return 'security';
    if (path.startsWith('/monitoring')) return 'monitoring';
    if (path.startsWith('/queue-manager')) return 'queue-manager';
    return 'web';
  }

  private startFlushTimer(): void {
    setInterval(() => {
      if (this.buffer.length > 0) {
        this.flush();
      }
    }, this.FLUSH_INTERVAL_MS);
  }

  /** Flush on `pagehide` / hidden visibility so buffered events are not lost. */
  private registerUnloadFlush(): void {
    try {
      window.addEventListener('pagehide', () => this.flush(true));
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') this.flush(true);
      });
    } catch {
      /* ignored */
    }
  }

  private flush(useBeacon = false): void {
    if (this.buffer.length === 0) return;

    const events = [...this.buffer];
    this.buffer = [];

    if (events.length === 1) {
      this.sendSingle(events[0] as TrackerEvent, useBeacon);
    } else {
      this.sendBatch(events as TrackerEvent[], useBeacon);
    }
  }

  private sendSingle(event: TrackerEvent, useBeacon = false): void {
    this.send(this.getTrackingUrl('/track'), event, useBeacon);
  }

  private sendBatch(events: TrackerEvent[], useBeacon = false): void {
    this.send(this.getTrackingUrl('/track/batch'), { events }, useBeacon);
  }

  private send(url: string, body: unknown, useBeacon: boolean): void {
    if (useBeacon && this.sendDuringUnload(url, body)) return;

    this.http.post(url, body).subscribe({
      error: () => {
        // Never break UX for event tracking.
      },
    });
  }

  /** `fetch` keepalive first (CORS with credentials), `sendBeacon` as fallback. */
  private sendDuringUnload(url: string, body: unknown): boolean {
    let payload: string;
    try {
      payload = JSON.stringify(body);
    } catch {
      return false;
    }

    try {
      if (typeof fetch === 'function' && 'keepalive' in Request.prototype) {
        fetch(url, {
          method: 'POST',
          body: payload,
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          keepalive: true,
        }).catch(() => {
          /* Never break UX for event tracking */
        });
        return true;
      }
    } catch {
      /* fall back to sendBeacon */
    }

    try {
      if (navigator?.sendBeacon) {
        return navigator.sendBeacon(url, new Blob([payload], { type: 'application/json' }));
      }
    } catch {
      /* ignored */
    }

    return false;
  }

  private getTrackingUrl(path: string): string {
    return `${this.env.API_EVENT_TRACKER}${path}`;
  }
}
